import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { StrataLogo } from "@/components/StrataLogo";

interface SmartChipButtonProps {
  selectedText: string;
  tableSchema?: {
    fields: Array<{ name: string; type: string; config?: any }>;
  };
  onEntitiesExtracted: (entities: Array<Record<string, any>>) => void;
}

export function SmartChipButton({ selectedText, tableSchema, onEntitiesExtracted }: SmartChipButtonProps) {
  const [isExtracting, setIsExtracting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const handleExtract = async () => {
    if (!selectedText.trim()) {
      toast.error("Please select some text first");
      return;
    }

    setIsExtracting(true);

    try {
      const { data, error } = await supabase.functions.invoke("extract-entities", {
        body: { text: selectedText, tableSchema }
      });

      if (error) throw error;

      if (data.error) {
        if (data.error.includes("Rate limit")) {
          toast.error("AI rate limit reached. Please try again later.");
        } else if (data.error.includes("credits")) {
          toast.error("AI credits exhausted. Please add credits in Settings.");
        } else {
          toast.error(data.error);
        }
        return;
      }

      if (data.entities && data.entities.length > 0) {
        onEntitiesExtracted(data.entities);
        toast.success(`Extracted ${data.entities.length} ${data.entities.length === 1 ? 'entity' : 'entities'}`, {
          description: `Confidence: ${Math.round(data.confidence * 100)}%`
        });
        setIsOpen(false);
      } else {
        toast.info("No entities found in selected text");
      }
    } catch (error: any) {
      console.error("Error extracting entities:", error);
      toast.error("Failed to extract entities. Please try again.");
    } finally {
      setIsExtracting(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={!selectedText.trim()}
        >
          <StrataLogo size="sm" />
          Smart Chip
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-3">
          <div className="space-y-1">
            <h4 className="font-medium leading-none">Convert to Entities</h4>
            <p className="text-sm text-muted-foreground">
              AI will extract structured data from your selected text
            </p>
          </div>
          <div className="text-sm bg-muted p-2 rounded max-h-20 overflow-y-auto">
            {selectedText.substring(0, 100)}
            {selectedText.length > 100 && "..."}
          </div>
          <Button
            onClick={handleExtract}
            disabled={isExtracting}
            className="w-full"
          >
            {isExtracting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Extracting...
              </>
            ) : (
              <>
                <StrataLogo size="sm" className="mr-2" />
                Extract Entities
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
